import {
	Component,
	OnInit,
	OnDestroy,
	signal,
	inject,
	computed,
	ViewChild,
	TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
	ReactiveFormsModule,
	FormBuilder,
	FormGroup,
	Validators,
	FormsModule,
} from '@angular/forms';
import { AppointmentService, Appointment } from '../../../services/appointment';
import { StaffService, Staff } from '../../../services/staff';
import { ServiceCategoryService, ServiceCategory } from '../../../services/service-category';
import { AdminHeader } from '../../../services/admin-header';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { BookingModal } from '../../../components/booking-modal/booking-modal';
import { ModalService } from '../../../services/modal.service';

type CalendarView = 'day' | 'week' | 'month';

interface CalendarDay {
	date: string;
	count: number;
	day_name: string;
	is_today: boolean;
}

@Component({
	selector: 'app-appointments',
	imports: [CommonModule, ReactiveFormsModule, FormsModule, BookingModal],
	templateUrl: './appointments.html',
	styleUrl: './appointments.css',
})
export class Appointments implements OnInit, OnDestroy {
	private appointmentService = inject(AppointmentService);
	private staffService = inject(StaffService);
	private categoryService = inject(ServiceCategoryService);
	private fb = inject(FormBuilder);
	private adminHeader = inject(AdminHeader);
	private http = inject(HttpClient);
	private modalService = inject(ModalService);

	@ViewChild('appointmentDetailsModalTemplate')
	appointmentDetailsModalTemplate!: TemplateRef<any>;

	// Data signals
	appointments = signal<Appointment[]>([]);
	staffList = signal<Staff[]>([]);
	categories = signal<ServiceCategory[]>([]);
	services = signal<any[]>([]);
	availableSlots = signal<any[]>([]);
	stats = signal<any>({});
	calendarData = signal<CalendarDay[]>([]);

	// Customer search
	customerSearchResults = signal<any[]>([]);
	selectedCustomer = signal<any | null>(null);
	isSearchingCustomer = signal(false);
	private customerSearchSubject = new Subject<string>();

	// UI state
	isLoading = signal(true);
	showModal = signal(false);
	selectedAppointment = signal<Appointment | null>(null);
	isSubmitting = signal(false);
	errorMessage = signal('');
	calendarView = signal<CalendarView>('month');
	selectedDate = signal(new Date().toISOString().split('T')[0]);
	currentMonth = signal(new Date());

	// Computed
	selectedDateAppointments = computed(() => {
		const date = this.selectedDate();
		const appointments = this.appointments();
		console.log('Computing selectedDateAppointments:', {
			date,
			appointments,
			filtered: appointments.filter((apt) => apt.appointment_date.split('T')[0] === date),
		});
		return appointments.filter((apt) => apt.appointment_date.split('T')[0] === date);
	});

	appointmentForm: FormGroup = this.fb.group({
		customer_id: [null],
		service_id: [null], // Optional for consultations
		staff_id: [null], // Optional - will auto-assign
		first_name: ['', Validators.required],
		last_name: ['', Validators.required],
		phone: ['', Validators.required],
		email: [''],
		appointment_date: ['', Validators.required],
		start_time: ['', Validators.required],
		notes: [''],
		is_consultation: [true],
	});

	customerSearchForm = this.fb.group({
		phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
	});

	statusColors: Record<string, string> = {
		pending: 'bg-yellow-100 text-yellow-800',
		confirmed: 'bg-blue-100 text-blue-800',
		completed: 'bg-green-100 text-green-800',
		cancelled: 'bg-red-100 text-red-800',
		no_show: 'bg-gray-100 text-gray-800',
	};

	ngOnInit() {
		this.loadData();
		this.loadStats();
		this.loadCalendarData();

		// Setup customer search debouncing
		this.customerSearchSubject
			.pipe(debounceTime(400), distinctUntilChanged())
			.subscribe((phone) => {
				if (phone.length === 10) {
					this.searchCustomerByPhone(phone);
				}
			});

		this.adminHeader.setHeaderAction({
			label: 'Book Consultation',
			icon: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
			callback: () => this.openModal(),
		});
	}

	ngOnDestroy() {
		this.adminHeader.clearHeaderAction();
		this.customerSearchSubject.complete();
		this.modalService.close();
	}

	loadData() {
		this.loadAppointments();
		this.staffService.getAll().subscribe((res) => {
			if (res.status) this.staffList.set(res.data);
		});
		this.categoryService.getAll().subscribe((res) => {
			if (res.status) this.categories.set(res.data);
		});
	}

	loadAppointments() {
		this.isLoading.set(true);
		console.log('Loading appointments for date:', this.selectedDate());
		this.appointmentService.getByDate(this.selectedDate()).subscribe({
			next: (res) => {
				console.log('Appointments API response:', res);
				if (res.status) {
					console.log('Setting appointments:', res.data);
					this.appointments.set(res.data);
				}
				this.isLoading.set(false);
				console.log('isLoading set to false, appointments:', this.appointments());
				console.log(
					'selectedDateAppointments after load:',
					this.selectedDateAppointments(),
				);
			},
			error: (err) => {
				console.error('Error loading appointments:', err);
				this.isLoading.set(false);
			},
		});
	}

	loadStats() {
		this.appointmentService.getStats().subscribe({
			next: (res) => {
				if (res.status) this.stats.set(res.data);
			},
		});
	}

	loadCalendarData() {
		const start = new Date(
			this.currentMonth().getFullYear(),
			this.currentMonth().getMonth(),
			1,
		);
		const end = new Date(
			this.currentMonth().getFullYear(),
			this.currentMonth().getMonth() + 1,
			0,
		);

		this.appointmentService
			.getCalendarData(start.toISOString().split('T')[0], end.toISOString().split('T')[0])
			.subscribe({
				next: (res) => {
					if (res.status) this.calendarData.set(res.data);
				},
			});
	}

	// Calendar navigation
	changeMonth(delta: number) {
		const newMonth = new Date(this.currentMonth());
		newMonth.setMonth(newMonth.getMonth() + delta);
		this.currentMonth.set(newMonth);
		this.loadCalendarData();
	}

	selectCalendarDate(date: string) {
		this.selectedDate.set(date);
		this.loadAppointments();
	}

	changeCalendarView(view: CalendarView) {
		this.calendarView.set(view);
	}

	goToToday() {
		this.selectedDate.set(new Date().toISOString().split('T')[0]);
		this.currentMonth.set(new Date());
		this.loadAppointments();
		this.loadCalendarData();
	}

	// Customer search
	onPhoneInput(event: Event) {
		const phone = (event.target as HTMLInputElement).value;
		this.customerSearchSubject.next(phone);
	}

	searchCustomerByPhone(phone: string) {
		this.isSearchingCustomer.set(true);
		this.http.get<any>(`${environment.apiUrl}/admin/customers/phone/${phone}`).subscribe({
			next: (res) => {
				if (res.status && res.data) {
					this.selectedCustomer.set(res.data);
					this.appointmentForm.patchValue({
						customer_id: res.data.id,
						first_name: res.data.first_name,
						last_name: res.data.last_name,
						phone: res.data.phone,
						email: res.data.email || '',
					});
				}
				this.isSearchingCustomer.set(false);
			},
			error: () => {
				this.selectedCustomer.set(null);
				this.isSearchingCustomer.set(false);
			},
		});
	}

	clearCustomerSearch() {
		this.selectedCustomer.set(null);
		this.customerSearchForm.reset();
		this.appointmentForm.patchValue({
			customer_id: null,
			first_name: '',
			last_name: '',
			phone: '',
			email: '',
		});
	}

	openModal() {
		this.appointmentForm.reset({
			appointment_date: this.selectedDate(),
			is_consultation: true,
		});
		this.selectedCustomer.set(null);
		this.showModal.set(true);
		this.errorMessage.set('');
		this.generateDefaultSlots();
		document.body.style.overflow = 'hidden';
	}

	closeModal() {
		this.showModal.set(false);
		this.appointmentForm.reset();
		this.selectedCustomer.set(null);
		this.errorMessage.set('');
		document.body.style.overflow = '';
	}

	onBookingModalClose() {
		this.showModal.set(false);
		document.body.style.overflow = '';
		// Refresh appointments data after booking
		this.loadData();
	}

	onCategoryChange(event: Event) {
		const categoryId = (event.target as HTMLSelectElement).value;
		if (categoryId) {
			this.http
				.get<any>(`${environment.apiUrl}/public/services/category/${categoryId}`)
				.subscribe({
					next: (res) => {
						if (res.status) this.services.set(res.data);
					},
				});
		} else {
			this.services.set([]);
		}
	}

	toggleConsultation(isConsultation: boolean) {
		this.appointmentForm.patchValue({ is_consultation: isConsultation });
		if (isConsultation) {
			this.appointmentForm.patchValue({ service_id: null });
		}
	}

	loadAvailableSlots() {
		const { service_id, staff_id, appointment_date, is_consultation } =
			this.appointmentForm.value;

		if (!appointment_date) {
			this.availableSlots.set([]);
			return;
		}

		// If no staff selected, show default time slots
		if (!staff_id) {
			this.generateDefaultSlots();
			return;
		}

		// If staff is selected, load available slots from backend
		const service = this.services().find((s) => s.id == service_id);
		const duration = service?.duration_minutes || 30;

		this.appointmentService
			.getAvailableSlots(appointment_date, staff_id, service_id || 0, duration)
			.subscribe({
				next: (res) => {
					if (res.status && res.data?.slots) {
						this.availableSlots.set(res.data.slots);
					} else if (res.status && Array.isArray(res.data)) {
						this.availableSlots.set(res.data);
					} else {
						this.availableSlots.set([]);
					}
				},
				error: (err) => {
					console.error('Error loading slots:', err);
					this.generateDefaultSlots();
				},
			});
	}

	generateDefaultSlots() {
		// Generate slots from 9 AM to 8 PM (30-minute intervals)
		const slots = [];
		for (let hour = 9; hour <= 20; hour++) {
			for (let min = 0; min < 60; min += 30) {
				if (hour === 20 && min > 0) break; // Stop at 8:00 PM
				const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
				const isPM = hour >= 12;
				const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
				const displayMin = min.toString().padStart(2, '0');
				slots.push({
					start: timeStr,
					display: `${displayHour}:${displayMin} ${isPM ? 'PM' : 'AM'}`,
					available: true,
				});
			}
		}
		this.availableSlots.set(slots);
	}

	saveAppointment() {
		if (this.appointmentForm.invalid) return;

		this.isSubmitting.set(true);
		this.appointmentService.create(this.appointmentForm.value).subscribe({
			next: () => {
				this.loadAppointments();
				this.loadStats();
				this.loadCalendarData();
				this.closeModal();
				this.isSubmitting.set(false);
			},
			error: (err) => {
				this.errorMessage.set(err.error?.message || 'Failed to book appointment');
				this.isSubmitting.set(false);
			},
		});
	}

	// Remarks modal state
	showRemarksModal = signal(false);
	remarksText = signal('');
	remarksAction = signal<{ appointment: Appointment; status: string } | null>(null);

	// Open remarks modal before completing or cancelling
	promptRemarks(appointment: Appointment, status: string) {
		this.remarksAction.set({ appointment, status });
		this.remarksText.set('');
		this.showRemarksModal.set(true);
		document.body.style.overflow = 'hidden';
	}

	closeRemarksModal() {
		this.showRemarksModal.set(false);
		this.remarksAction.set(null);
		this.remarksText.set('');
		document.body.style.overflow = '';
	}

	submitRemarks() {
		const action = this.remarksAction();
		if (!action) return;

		this.appointmentService
			.updateStatus(action.appointment.id, action.status, this.remarksText() || undefined)
			.subscribe({
				next: () => {
					this.loadAppointments();
					this.loadStats();
					this.loadCalendarData();
					this.closeRemarksModal();
				},
				error: (err) => alert(err.error?.message || 'Failed to update status'),
			});
	}

	updateStatus(appointment: Appointment, status: string) {
		// For complete/cancel, ask for remarks
		if (status === 'completed' || status === 'cancelled') {
			this.promptRemarks(appointment, status);
			return;
		}

		this.appointmentService.updateStatus(appointment.id, status).subscribe({
			next: () => {
				this.loadAppointments();
				this.loadStats();
				this.loadCalendarData();
			},
			error: (err) => alert(err.error?.message || 'Failed to update status'),
		});
	}

	cancelAppointment(appointment: Appointment) {
		this.promptRemarks(appointment, 'cancelled');
	}

	deleteAppointment(appointment: Appointment) {
		if (!confirm(`Delete appointment for ${appointment.full_name}?`)) return;
		this.appointmentService.delete(appointment.id).subscribe({
			next: () => {
				this.loadAppointments();
				this.loadStats();
				this.loadCalendarData();
			},
			error: (err) => alert(err.error?.message || 'Failed to delete appointment'),
		});
	}

	viewDetails(appointment: Appointment) {
		this.selectedAppointment.set(appointment);
		this.modalService.open(this.appointmentDetailsModalTemplate, '600px', () => {
			this.selectedAppointment.set(null);
		});
	}

	closeDetails() {
		this.modalService.close();
	}

	copyZoomLink(url: string) {
		navigator.clipboard
			.writeText(url)
			.then(() => {
				// Optional: Show a brief success message
				alert('Zoom link copied to clipboard!');
			})
			.catch((err) => {
				console.error('Failed to copy:', err);
			});
	}
}
